import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'quill/dist/quill.bubble.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ArrowLeft } from 'lucide-react';
import { getBlogById } from '../../../services/Writer/blogService';
import { getCategories } from '../../../services/Writer/blogCategoriesService';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { parseQuillOpsToDelta } from '../../../utils/quillContent';

type Category = { categoryId: string; name: string };

type Blog = {
  blogId: string;
  title: string;
  content: string;
  featuredImage?: string | null;
  status: 'draft' | 'published';
  adminApproval?: 'pending' | 'approved' | 'rejected' | null;
  rejectionReason?: string | null;
  blogDate?: string | null;
  categoryId: string;
  category?: { name?: string | null } | null;
  authorType?: string | null;
  createdAt: string;
  updatedAt: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  slug?: string | null;
  focusKeyword?: string | null;
  secondaryKeywords?: string | null;
  recommendedSchema?: string | null;
  referenceLinks?: string[] | null;
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
};

const ViewBlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [blog, setBlog] = useState<Blog | null>((location.state as any) || null);
  const [categoryName, setCategoryName] = useState<string>('—');

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // Always fetch by id so SEO fields (not present on list state) are available.
        const data = await getBlogById(id);
        setBlog(data);

        // category name
        try {
          const cats: Category[] = await getCategories();
          const found = cats.find((c) => c.categoryId === data.categoryId);
          setCategoryName(found?.name || data.category?.name || 'Unknown');
        } catch {
          setCategoryName('—');
        }
      } catch (e: any) {
        toast.error(getApiErrorMessage(e, 'Failed to load blog'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return <div className="py-16 text-center text-gray-600 dark:text-gray-400">Loading...</div>;
  }

  if (!blog) {
    return <div className="py-16 text-center text-gray-600 dark:text-gray-400">Blog not found.</div>;
  }

  const quillValue = parseQuillOpsToDelta(blog.content || '');

  const seoRows: { label: string; value: string }[] = [
    { label: 'Meta Title', value: (blog.metaTitle || '').trim() },
    { label: 'Meta Description', value: (blog.metaDescription || '').trim() },
    {
      label: 'URL Slug',
      value: (blog.slug || '').trim() ? `/blog/${String(blog.slug).replace(/^\/?blog\//i, '')}` : '',
    },
    { label: 'Focus Keyword', value: (blog.focusKeyword || '').trim() },
    { label: 'Secondary Keywords', value: (blog.secondaryKeywords || '').trim() },
    { label: 'Recommended Schema', value: (blog.recommendedSchema || '').trim() },
  ];
  const hasSeo = seoRows.some((row) => Boolean(row.value));

  return (
    <div className="max-w-4xl mx-auto">
      <ToastContainer position="top-right" autoClose={2500} />

      <style
        dangerouslySetInnerHTML={{
          __html: `
          /* Force Quill content readability in dark mode even if delta stores dark colors */
          .blog-view .ql-container {
            border: none !important;
            font-size: 16px;
          }
          .blog-view .ql-editor {
            padding: 0 !important;
          }
          .dark .blog-view .ql-editor {
            color: #f9fafb !important;
          }
          .dark .blog-view .ql-editor * {
            color: inherit !important;
          }
          .dark .blog-view .ql-editor [style*="color"] {
            color: inherit !important;
          }
          .dark .blog-view .ql-editor [style*="background"] {
            background: transparent !important;
          }
          .dark .blog-view a {
            color: #60a5fa !important;
          }
        `,
        }}
      />

      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() =>
            navigate(blog.authorType === 'doctor' ? '/writer/doctor-blogs' : '/writer/blogs')
          }
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {blog.status.toUpperCase()}
          {blog.adminApproval ? ` • ${String(blog.adminApproval).toUpperCase()}` : ''}
          {' • '}
          {formatDate(blog.blogDate || blog.createdAt)}
        </div>
      </div>

      {blog.adminApproval === 'rejected' ? (
        <div className="mb-4 rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          <div className="font-semibold">Rejected</div>
          <div className="mt-1">
            {blog.rejectionReason?.trim()
              ? `Reason: ${blog.rejectionReason.trim()}`
              : 'No rejection reason was provided.'}
          </div>
        </div>
      ) : null}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
        {blog.featuredImage ? (
          <div className="w-full bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
            <img
              src={blog.featuredImage}
              alt={blog.title}
              className="w-full h-auto max-h-[70vh] object-contain"
            />
          </div>
        ) : null}

        <div className="p-6 sm:p-8">
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Category: {categoryName}</div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">{blog.title}</h1>

          <div className="prose max-w-none dark:prose-invert blog-view">
            <ReactQuill value={quillValue as any} readOnly theme="bubble" modules={{ toolbar: false }} />
          </div>

          {Array.isArray(blog.referenceLinks) && blog.referenceLinks.length > 0 ? (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Reference Links
              </h2>
              <ul className="space-y-2">
                {blog.referenceLinks.map((link) => (
                  <li key={link}>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-all text-sm text-primary-600 underline hover:text-primary-700 dark:text-primary-400"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">SEO Meta Package</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Search / social metadata saved with this blog.
            </p>

            {hasSeo ? (
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {seoRows.map((row) => (
                  <div
                    key={row.label}
                    className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-4 ${
                      row.label === 'Meta Description' || row.label === 'Secondary Keywords'
                        ? 'sm:col-span-2'
                        : ''
                    }`}
                  >
                    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {row.label}
                    </dt>
                    <dd className="mt-1.5 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
                      {row.value || '—'}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 px-4 py-6 text-sm text-gray-500 dark:text-gray-400">
                No SEO fields added for this blog yet. Edit the blog to fill Meta Title, Description, Slug, and keywords.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewBlogPage;

